import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { computed, type IComputedValue } from 'mobx';
import type {
  IChipInstanceModel,
  IChipModel,
  ITileManagerModel,
} from '@/interfaces/models';
import type { Connection } from '@/models/connections/ConnectionGraph';
import { TileType } from '@/models/TileTypes';
import { Side } from '@/schemas/tiles';
import type { InterfaceLinkInstanceId } from '@/types/ids';
import { MAX_LOWERED_GRAPH_ITEMS } from './graphBudgets';

export interface ProspectiveContainmentResult {
  incompatibleAttachments?: Connection[];
  incompatibleKinds?: Set<string>;
  cyclic: boolean;
  budgetExceeded: boolean;
  /** Physical edges in the cyclic component; the caller rejects its NEW edge. */
  cyclicAttachments: Connection[];
  cycleGroups: Set<string>;
  /** Stable authored relations participating in true SCC cycles. */
  cyclicRelationKeys: Set<string>;
}

interface ContainmentSummary {
  expandedItems: number;
  peakGuard: number;
  ports: Map<string, string>;
  interfaces: Map<string, Map<Side, string>>;
  relations: Array<[string, string]>;
  budgetExceeded: boolean;
}
type SummaryDependency = {
  chip: IChipModel;
  template: IChipModel['templateGraph'];
  version: number;
  configuration: string;
  connections: number | undefined;
  structure: number | undefined;
};
const summaryCache = new WeakMap<
  IChipModel,
  { dependencies: SummaryDependency[]; summary: ContainmentSummary }
>();
const configurationCache = new WeakMap<IChipModel, IComputedValue<string>>();
function summaryConfiguration(chip: IChipModel) {
  let entry = configurationCache.get(chip);
  if (!entry) {
    entry = computed(
      () => {
        const circuit = chip.logicCircuit?.circuitModel;
        return JSON.stringify([
          chip.kind,
          [...(chip.interfaceNodeMap ?? [])],
          circuit?.tileManagerModel.tiles.map((tile) => [
            tile.id,
            tile.tileType,
            tile.meta.partOfChip,
            tile.meta.bundle,
            tile.meta.cable,
            (tile.state as { inputMode?: unknown })?.inputMode,
            (tile.state as { connectedInterface?: unknown })?.connectedInterface,
          ]),
          circuit?.chips.map((instance) => [
            instance.id,
            instance.chipId,
            instance.bundleWidth,
          ]),
        ]);
      },
      { keepAlive: true },
    );
    configurationCache.set(chip, entry);
  }
  return entry.get();
}
function summaryDependencies(chip: IChipModel, ancestors: Set<IChipModel>) {
  const dependencies: SummaryDependency[] = [];
  const pending = [chip],
    seen = new Set<IChipModel>();
  let recursive = false;
  for (let i = 0; i < pending.length; i++) {
    const next = pending[i];
    if (ancestors.has(next)) recursive = true;
    if (seen.has(next)) continue;
    seen.add(next);
    const circuit = next.logicCircuit?.circuitModel;
    dependencies.push({
      chip: next,
      template: next.templateGraph,
      version: next.templateGraphVersion,
      configuration: summaryConfiguration(next),
      connections: circuit?.connectionModel?.topologyVersion,
      structure: circuit?.tileManagerModel.structureVersion,
    });
    for (const instance of circuit?.chips ?? [])
      if (instance.chip) pending.push(instance.chip);
  }
  return { dependencies, recursive };
}
/** Keep deeply nested or recursive definitions on the original iterative expansion path. */
function canSummarize(chip: IChipModel) {
  const pending = [{ chip, path: new Set<IChipModel>() }];
  for (let i = 0; i < pending.length; i++) {
    const { chip: current, path } = pending[i];
    if (path.has(current) || path.size >= 32) return false;
    const nextPath = new Set([...path, current]);
    for (const instance of current.logicCircuit?.circuitModel?.chips ?? [])
      if (instance.chip) pending.push({ chip: instance.chip, path: nextPath });
    if (pending.length > MAX_LOWERED_GRAPH_ITEMS) return false;
  }
  return true;
}
function chipContainmentSummary(
  chip: IChipModel,
  ancestors: Set<IChipModel>,
): ContainmentSummary {
  const { dependencies, recursive } = summaryDependencies(chip, ancestors);
  const cached = summaryCache.get(chip);
  if (
    !recursive &&
    cached &&
    cached.dependencies.length === dependencies.length &&
    dependencies.every((item, index) => {
      const previous = cached.dependencies[index];
      return (
        item.chip === previous.chip &&
        item.template === previous.template &&
        item.version === previous.version &&
        item.configuration === previous.configuration &&
        item.connections === previous.connections &&
        item.structure === previous.structure
      );
    })
  )
    return cached.summary;
  const circuit = chip.logicCircuit.circuitModel;
  const summary = buildProspectiveContainment(
    circuit.tileManagerModel,
    circuit.connectionModel?.dto.connections ?? [],
    circuit.chips ?? [],
    new Set([...ancestors, chip]),
    true,
  ) as ContainmentSummary;
  if (!recursive && dependencies.every((item) => item.connections !== undefined))
    summaryCache.set(chip, { dependencies, summary });
  return summary;
}

/** Pure authored-topology preflight. Never reads or changes live lane bindings. */
export function analyzeProspectiveContainment(
  manager: ITileManagerModel,
  edges: readonly Connection[],
  instances: readonly IChipInstanceModel[],
): ProspectiveContainmentResult {
  const result = buildProspectiveContainment(
    manager,
    edges,
    instances,
    new Set(),
    false,
    true,
  ) as ProspectiveContainmentResult;
  // Near the cap, retain the exact original iterative budget/order semantics.
  return result.budgetExceeded
    ? (buildProspectiveContainment(
        manager,
        edges,
        instances,
        new Set(),
        false,
        false,
      ) as ProspectiveContainmentResult)
    : result;
}

/** Definition compatibility follows physical transport, including nested passive channels.
 * Expand authored boundaries rather than relying on label-derived runtime aliases.
 */
export function analyzeBundleCompatibility(
  manager: ITileManagerModel,
  edges: readonly Connection[],
  instances: readonly IChipInstanceModel[],
) {
  const result = buildProspectiveContainment(
    manager,
    edges,
    instances,
    new Set(),
    false,
    false,
    true,
  ) as ProspectiveContainmentResult;
  return {
    blocked: result.budgetExceeded ? [...edges] : (result.incompatibleAttachments ?? []),
    kinds: result.incompatibleKinds ?? new Set<string>(),
    budgetExceeded: result.budgetExceeded,
  };
}

function buildProspectiveContainment(
  manager: ITileManagerModel,
  edges: readonly Connection[],
  instances: readonly IChipInstanceModel[],
  ancestors: Set<IChipModel>,
  summaryOnly: boolean,
  useSummaries = true,
  checkTypes = false,
): ProspectiveContainmentResult | ContainmentSummary {
  const empty = (): ProspectiveContainmentResult => ({
    cyclic: false,
    budgetExceeded: false,
    cyclicAttachments: [],
    cycleGroups: new Set(),
    cyclicRelationKeys: new Set(),
  });
  const adapterReachability = new Map<IChipModel | undefined, boolean>();
  const localSummaries = new Map<IChipModel, ContainmentSummary>();
  const summaryEligible = new Map<IChipModel, boolean>();
  const hasAdapter = (start: IChipModel | undefined): boolean => {
    if (adapterReachability.has(start)) return adapterReachability.get(start)!;
    const pending = start ? [start] : [],
      seen = new Set<IChipModel>();
    for (let i = 0; i < pending.length; i++) {
      const chip = pending[i];
      if (chip.kind === 'bundle-adapter') {
        adapterReachability.set(start, true);
        return true;
      }
      if (seen.has(chip)) continue;
      seen.add(chip);
      for (const child of chip.logicCircuit?.circuitModel?.chips ?? [])
        if (child.chip) pending.push(child.chip);
      if (seen.size > MAX_LOWERED_GRAPH_ITEMS) return true;
    }
    adapterReachability.set(start, false);
    return false;
  };
  if (!summaryOnly && !instances.some((instance) => hasAdapter(instance.chip)))
    return empty();
  if (edges.length > MAX_LOWERED_GRAPH_ITEMS) return { ...empty(), budgetExceeded: true };
  const parent = new Map<string, string>();
  const ports = new Map<string, string>();
  const busTypes = new Map<string, string>();
  const typedMembers = new Map<
    string,
    { bus: string; member: string; identity: string }
  >();
  const relations: Array<[string, string]> = [];
  let namespace: string[] = [];
  const pointKey = (id: string, side: Side) => JSON.stringify([namespace, id, side]);
  const add = (key: string) => {
    if (!parent.has(key)) parent.set(key, key);
    return key;
  };
  const find = (key: string): string => {
    let root = key;
    while (parent.get(root) !== root) root = parent.get(root)!;
    while (key !== root) {
      const next = parent.get(key)!;
      parent.set(key, root);
      key = next;
    }
    return root;
  };
  const union = (a: string, b: string) => parent.set(find(a), find(b));
  const frames = [
    {
      manager,
      edges,
      instances,
      namespace: [] as string[],
      ancestors,
    },
  ];
  let hiddenItems = 0;
  let peakGuard = 0;
  const boundaryLinks: Array<[string, string]> = [];
  const authoredEdges: Array<[string, string]> = [];
  for (let frameIndex = 0; frameIndex < frames.length; frameIndex++) {
    const frame = frames[frameIndex];
    namespace = frame.namespace;
    const tiles = frame.manager.tiles;
    peakGuard = Math.max(
      peakGuard,
      tiles.length + parent.size + authoredEdges.length + hiddenItems,
    );
    if (
      tiles.length + parent.size + authoredEdges.length + hiddenItems >
      MAX_LOWERED_GRAPH_ITEMS
    )
      return { ...empty(), budgetExceeded: true };
    const byOwner = new Map<string, typeof tiles>();
    for (const tile of tiles) {
      if (tile.meta.partOfChip) {
        const list = byOwner.get(tile.meta.partOfChip) ?? [];
        list.push(tile);
        byOwner.set(tile.meta.partOfChip, list);
      } else if (
        (tile.tileType === TileType.Wire ||
          (tile.tileType === TileType.SingleInterface &&
            (!(tile.state as { inputMode?: string }).inputMode ||
              (tile.state as { inputMode?: string }).inputMode === 'none'))) &&
        (!tile.meta.bundle || checkTypes)
      ) {
        const key = add(JSON.stringify([namespace, 'wire', tile.id]));
        for (const side of Object.values(Side)) ports.set(pointKey(tile.id, side), key);
      }
    }
    if (checkTypes)
      for (const tile of tiles) {
        if (!tile.meta.partOfChip && tile.tileType === TileType.Bridge) {
          for (const [axis, sides] of [
            [0, [Side.LEFT, Side.RIGHT]],
            [1, [Side.TOP, Side.BOTTOM]],
          ] as const) {
            const key = add(JSON.stringify([namespace, 'bridge', tile.id, axis]));
            for (const side of sides) ports.set(pointKey(tile.id, side), key);
          }
        }
      }
    if (checkTypes)
      for (const tile of tiles) {
        if (!tile.meta.partOfChip && tile.tileType === TileType.BundleInterface) {
          // A selected scalar tap is a boundary: its RIGHT side never carries a bundle type.
          ports.set(
            pointKey(tile.id, Side.LEFT),
            add(JSON.stringify([namespace, 'tap', tile.id])),
          );
        }
      }
    for (const tile of tiles)
      if (!tile.meta.partOfChip && tile.tileType === TileType.MultiInterface)
        for (const side of Object.values(Side))
          ports.set(
            pointKey(tile.id, side),
            add(JSON.stringify([namespace, 'multi', tile.id, side])),
          );
    for (const instance of frame.instances) {
      const chip = instance.chip;
      if (!chip || instance.bundleWidth) continue;
      const placed = byOwner.get(instance.id) ?? [];
      if (chip.kind === 'bundle-adapter') {
        const bus = add(JSON.stringify([namespace, 'bus', instance.id]));
        if (checkTypes) busTypes.set(bus, chip.id);
        const members = new Set<string>();
        for (const tile of placed) {
          if (tile.tileType === TileType.BundleInterface) {
            ports.set(pointKey(tile.id, Side.LEFT), bus);
            continue;
          }
          const link = (
            tile.state as { connectedInterface?: { interfaceLinkId?: string } }
          )?.connectedInterface?.interfaceLinkId;
          if (!link) continue;
          for (const [side, node] of Object.entries(
            chip.interfaceNodeMap.get(link as InterfaceLinkInstanceId) ?? {},
          )) {
            if (!node) continue;
            const authoredIdentity =
              chip.templateGraph?.nodes.get(node)?.runtimeKey ?? node;
            const member = add(
              JSON.stringify([namespace, 'member', instance.id, authoredIdentity]),
            );
            ports.set(pointKey(tile.id, side as Side), member);
            members.add(member);
            if (checkTypes)
              typedMembers.set(member, {
                bus,
                member,
                identity: JSON.stringify([chip.id, authoredIdentity]),
              });
          }
        }
        for (const member of members) relations.push([member, bus]);
        continue;
      }
      if (hasAdapter(chip) && !frame.ancestors.has(chip)) {
        if (useSummaries && !summaryEligible.has(chip))
          summaryEligible.set(chip, canSummarize(chip));
        if (!useSummaries || !summaryEligible.get(chip)) {
          const circuit = chip.logicCircuit.circuitModel;
          const innerNamespace = [...namespace, instance.id];
          frames.push({
            manager: circuit.tileManagerModel,
            edges: circuit.connectionModel?.dto.connections ?? [],
            instances: circuit.chips ?? [],
            namespace: innerNamespace,
            ancestors: new Set([...frame.ancestors, chip]),
          });
          for (const innerTile of circuit.tileManagerModel.tiles) {
            if (innerTile.meta.partOfChip) continue;
            const innerLink = (
              innerTile.state as { connectedInterface?: { interfaceLinkId?: string } }
            )?.connectedInterface?.interfaceLinkId;
            if (!innerLink) continue;
            for (const outerTile of placed) {
              const outerLink = (
                outerTile.state as { connectedInterface?: { interfaceLinkId?: string } }
              )?.connectedInterface?.interfaceLinkId;
              if (innerLink !== outerLink) continue;
              for (const side of Object.values(Side))
                boundaryLinks.push([
                  pointKey(outerTile.id, side),
                  JSON.stringify([innerNamespace, innerTile.id, side]),
                ]);
            }
          }
          continue;
        }
        let summary = localSummaries.get(chip);
        if (!summary) {
          summary = chipContainmentSummary(chip, frame.ancestors);
          localSummaries.set(chip, summary);
        }
        if (
          summary.budgetExceeded ||
          summary.peakGuard +
            parent.size +
            authoredEdges.length +
            relations.length +
            frames.length +
            hiddenItems >
            MAX_LOWERED_GRAPH_ITEMS
        )
          return { ...empty(), budgetExceeded: true };
        peakGuard = Math.max(
          peakGuard,
          summary.peakGuard + parent.size + authoredEdges.length + hiddenItems,
        );
        const groups = new Map<string, string>();
        const group = (key: string) => {
          let result = groups.get(key);
          if (!result) {
            result = add(JSON.stringify([namespace, 'contained', instance.id, key]));
            groups.set(key, result);
          }
          return result;
        };
        for (const [child, container] of summary.relations)
          relations.push([group(child), group(container)]);
        const outerByLink = new Map<string, typeof placed>();
        for (const tile of placed) {
          const link = (
            tile.state as { connectedInterface?: { interfaceLinkId?: string } }
          )?.connectedInterface?.interfaceLinkId;
          if (link) outerByLink.set(link, [...(outerByLink.get(link) ?? []), tile]);
        }
        for (const [link, sides] of summary.interfaces)
          for (const [side, key] of sides)
            for (const outer of outerByLink.get(link) ?? [])
              ports.set(pointKey(outer.id, side), group(key));
        hiddenItems += summary.expandedItems - groups.size - summary.relations.length;
        if (
          parent.size +
            authoredEdges.length +
            relations.length +
            frames.length +
            hiddenItems >
          MAX_LOWERED_GRAPH_ITEMS
        )
          return { ...empty(), budgetExceeded: true };
        continue;
      }
      // Only actual WIRE-induced authored channels forward independent payload.
      // Active gates remain boundaries, even beside a passive nested bridge path.
      const graph = chip.templateGraph;
      if (!graph) continue;
      peakGuard = Math.max(
        peakGuard,
        graph.nodes.size + graph.edges.size + parent.size + hiddenItems,
      );
      if (
        graph.nodes.size + graph.edges.size + parent.size + hiddenItems >
        MAX_LOWERED_GRAPH_ITEMS
      )
        return { ...empty(), budgetExceeded: true };
      const wire = new Map<NodeHandle, string>();
      for (const node of graph.nodes.values())
        if (node.type === 'WIRE')
          wire.set(
            node.id,
            add(JSON.stringify([namespace, 'authored', instance.id, node.id])),
          );
      for (const edge of graph.edges.values()) {
        const a = wire.get(edge.sourceNodeId),
          b = wire.get(edge.targetNodeId);
        if (a && b) union(a, b);
      }
      for (const tile of placed) {
        const link = (tile.state as { connectedInterface?: { interfaceLinkId?: string } })
          ?.connectedInterface?.interfaceLinkId;
        if (!link) continue;
        for (const [side, node] of Object.entries(
          chip.interfaceNodeMap.get(link as InterfaceLinkInstanceId) ?? {},
        )) {
          const key = node && wire.get(node);
          if (key) ports.set(pointKey(tile.id, side as Side), key);
        }
      }
    }
    for (const edge of frame.edges)
      authoredEdges.push([
        pointKey(edge.a.tileId, edge.a.side),
        pointKey(edge.b.tileId, edge.b.side),
      ]);
    if (
      parent.size +
        authoredEdges.length +
        relations.length +
        frames.length +
        hiddenItems >
      MAX_LOWERED_GRAPH_ITEMS
    )
      return { ...empty(), budgetExceeded: true };
  }
  // Resolve exposed custom-chip interfaces after all nested frames are discovered.
  // A boundary can pass through several wrapper levels; no simulation nodes are read.
  for (let remaining = boundaryLinks.length; remaining; ) {
    let resolved = 0;
    for (let i = 0; i < boundaryLinks.length; i++) {
      const [outer, inner] = boundaryLinks[i];
      const key = ports.get(inner);
      if (!key || ports.has(outer)) continue;
      ports.set(outer, key);
      resolved++;
    }
    if (!resolved) break;
    remaining -= resolved;
  }
  namespace = [];
  if (
    parent.size + authoredEdges.length + relations.length + hiddenItems >
    MAX_LOWERED_GRAPH_ITEMS
  )
    return { ...empty(), budgetExceeded: true };
  for (const [pointA, pointB] of authoredEdges) {
    const a = ports.get(pointA);
    const b = ports.get(pointB);
    if (a && b) union(a, b);
  }
  if (checkTypes) {
    // Equal containing definitions carry equal member channels. Iterate to a
    // fixed point so nested bundles are checked at every member depth.
    let changed = true;
    let work = 0;
    while (changed) {
      changed = false;
      const channels = new Map<string, string>();
      for (const { bus, member, identity } of typedMembers.values()) {
        if (++work > MAX_LOWERED_GRAPH_ITEMS) return { ...empty(), budgetExceeded: true };
        const key = JSON.stringify([find(bus), identity]);
        const previous = channels.get(key);
        if (previous && find(previous) !== find(member)) {
          union(previous, member);
          changed = true;
        } else channels.set(key, member);
      }
    }
    const types = new Map<string, Set<string>>();
    for (const [bus, type] of busTypes) {
      const root = find(bus),
        values = types.get(root) ?? new Set<string>();
      values.add(type);
      types.set(root, values);
    }
    const conflicts = new Set(
      [...types].filter(([, values]) => values.size > 1).map(([key]) => key),
    );
    // The outer attachment that joins incompatible child payloads must be
    // rejected too, even when its own containing definition is compatible.
    const affected = new Set(conflicts);
    changed = true;
    while (changed) {
      changed = false;
      for (const [member, bus] of relations) {
        if (++work > MAX_LOWERED_GRAPH_ITEMS) return { ...empty(), budgetExceeded: true };
        const a = find(member),
          b = find(bus);
        if (affected.has(a) || affected.has(b))
          for (const key of [a, b]) {
            if (!affected.has(key)) {
              affected.add(key);
              changed = true;
            }
          }
      }
    }
    const incompatibleKinds = new Set(
      [...conflicts].map((key) => JSON.stringify([...types.get(key)!].sort())),
    );
    return {
      ...empty(),
      incompatibleKinds,
      incompatibleAttachments: edges.filter((edge) =>
        [edge.a, edge.b].some((point) => {
          const port = ports.get(pointKey(point.tileId, point.side));
          return port !== undefined && affected.has(find(port));
        }),
      ),
    };
  }
  if (summaryOnly) {
    const interfaces = new Map<string, Map<Side, string>>();
    for (const tile of manager.tiles) {
      if (tile.meta.partOfChip) continue;
      const link = (tile.state as { connectedInterface?: { interfaceLinkId?: string } })
        ?.connectedInterface?.interfaceLinkId;
      if (!link) continue;
      let sides = interfaces.get(link);
      if (!sides) interfaces.set(link, (sides = new Map()));
      for (const side of Object.values(Side)) {
        const key = ports.get(JSON.stringify([[], tile.id, side]));
        if (key && !sides.has(side)) sides.set(side, find(key));
      }
    }
    return {
      expandedItems:
        parent.size +
        authoredEdges.length +
        relations.length +
        frames.length +
        hiddenItems,
      peakGuard: Math.max(
        peakGuard,
        parent.size +
          authoredEdges.length +
          relations.length +
          frames.length +
          hiddenItems,
      ),
      ports: new Map([...ports].map(([key, value]) => [key, find(value)])),
      interfaces,
      relations: relations.map(([child, container]) => [find(child), find(container)]),
      budgetExceeded: false,
    };
  }
  const outgoing = new Map<string, Set<string>>();
  const incoming = new Map<string, number>();
  for (const key of parent.keys()) {
    const root = find(key);
    outgoing.set(root, outgoing.get(root) ?? new Set());
    incoming.set(root, incoming.get(root) ?? 0);
  }
  for (const [child, container] of relations) {
    const a = find(child),
      b = find(container);
    if (!outgoing.get(a)!.has(b)) {
      outgoing.get(a)!.add(b);
      incoming.set(b, incoming.get(b)! + 1);
    }
  }
  // Iterative Kosaraju: downstream dependents of an existing invalid bus are
  // not themselves cycles and must remain available for repair/reconnection.
  const reverse = new Map([...outgoing.keys()].map((key) => [key, new Set<string>()]));
  for (const [key, neighbors] of outgoing)
    for (const next of neighbors) reverse.get(next)!.add(key);
  const seen = new Set<string>(),
    ordered: string[] = [];
  for (const start of outgoing.keys()) {
    if (seen.has(start)) continue;
    const stack: Array<[string, boolean]> = [[start, false]];
    while (stack.length) {
      const [key, finish] = stack.pop()!;
      if (finish) {
        ordered.push(key);
        continue;
      }
      if (seen.has(key)) continue;
      seen.add(key);
      stack.push([key, true]);
      for (const next of outgoing.get(key)!)
        if (!seen.has(next)) stack.push([next, false]);
    }
  }
  const component = new Map<string, number>(),
    cycleGroups = new Set<string>();
  let componentId = 0;
  for (let i = ordered.length - 1; i >= 0; i--) {
    const start = ordered[i];
    if (component.has(start)) continue;
    const pending = [start],
      group: string[] = [];
    component.set(start, componentId);
    for (let cursor = 0; cursor < pending.length; cursor++) {
      const key = pending[cursor];
      group.push(key);
      for (const next of reverse.get(key)!)
        if (!component.has(next)) {
          component.set(next, componentId);
          pending.push(next);
        }
    }
    if (group.length > 1 || outgoing.get(start)!.has(start))
      for (const key of group) cycleGroups.add(key);
    componentId++;
  }
  const cyclicRelationKeys = new Set(
    relations
      .filter(
        ([child, outer]) =>
          cycleGroups.has(find(child)) &&
          component.get(find(child)) === component.get(find(outer)),
      )
      .map((relation) => JSON.stringify(relation)),
  );
  return {
    cyclic: cycleGroups.size > 0,
    budgetExceeded: false,
    cycleGroups,
    cyclicRelationKeys,
    cyclicAttachments: edges.filter((edge) => {
      const a = ports.get(pointKey(edge.a.tileId, edge.a.side));
      const b = ports.get(pointKey(edge.b.tileId, edge.b.side));
      return !!((a && cycleGroups.has(find(a))) || (b && cycleGroups.has(find(b))));
    }),
  };
}
